/**
 * 限流倒计时（42901 + `Retry-After`）。
 *
 * 为什么是模块级单例而不是组件内状态：一次问答触发限流后，用户可能切到别的页面
 * 再切回来，倒计时必须还在走。服务端给的是秒数，这里换算成**截止时间戳**存起来，
 * 避免每次重算都引入秒级漂移。
 *
 * 与 React 版的差别只在订阅方式：那边是 `useSyncExternalStore` + 冻结快照，
 * 这边是 Vue 的 `reactive` + `computed`。**语义完全一致**——任何组件读到的都是
 * 同一个对象，倒计时归零时自动解除。
 */

import { computed, reactive, readonly } from 'vue';

/** 兜底倒计时：服务端漏发 `Retry-After` 时使用。 */
const DEFAULT_RETRY_AFTER_SECONDS = 1;
/** 上限保护：避免一次异常响应把用户锁在门外一小时以上。 */
const MAX_RETRY_AFTER_SECONDS = 3600;

export interface RateLimitState {
  active: boolean;
  secondsRemaining: number;
}

const state = reactive<{ deadline: number; snapshot: RateLimitState }>({
  deadline: 0,
  snapshot: { active: false, secondsRemaining: 0 },
});

let timer: ReturnType<typeof setInterval> | null = null;

function stopTimer(): void {
  if (timer !== null) {
    clearInterval(timer);
    timer = null;
  }
}

function tick(): void {
  if (state.deadline === 0) {
    stopTimer();
    return;
  }
  const seconds = Math.max(0, Math.ceil((state.deadline - Date.now()) / 1000));
  if (seconds <= 0) {
    state.deadline = 0;
    state.snapshot = { active: false, secondsRemaining: 0 };
    stopTimer();
    return;
  }
  state.snapshot = { active: true, secondsRemaining: seconds };
}

/**
 * 进入限流态。
 *
 * 非有限或非正数一律退化成 1 秒：**不能**退化成 0，否则输入框会在服务端
 * 仍然限流的情况下立刻解禁，用户紧接着又撞一次 42901，体感是"点了没反应"。
 */
export function applyRateLimit(retryAfterSeconds: number): void {
  const seconds =
    Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0
      ? Math.min(Math.ceil(retryAfterSeconds), MAX_RETRY_AFTER_SECONDS)
      : DEFAULT_RETRY_AFTER_SECONDS;

  state.deadline = Date.now() + seconds * 1000;
  state.snapshot = { active: true, secondsRemaining: seconds };
  if (timer === null) {
    timer = setInterval(tick, 1000);
  }
}

/** 手动解除（用户重新登录、或测试收尾）。 */
export function clearRateLimit(): void {
  stopTimer();
  state.deadline = 0;
  state.snapshot = { active: false, secondsRemaining: 0 };
}

/** 响应式快照：组件里直接读 `rateLimit.snapshot.active`。 */
export const rateLimit = readonly(state);

/** 便捷计算属性，供模板直接使用。 */
export const rateLimitActive = computed<boolean>(() => state.snapshot.active);
export const secondsRemaining = computed<number>(() => state.snapshot.secondsRemaining);

/** 组合式入口，函数名与旧的 Hook 同名，调用点几乎不用改。 */
export function useRateLimit(): { active: boolean; secondsRemaining: number } {
  return { active: rateLimitActive.value, secondsRemaining: secondsRemaining.value };
}

/** 仅供测试：直接给定剩余秒数且**不启动定时器**，避免 vitest 因活跃 handle 不退出。 */
export function setRateLimitForTest(seconds: number): void {
  stopTimer();
  if (seconds <= 0) {
    state.deadline = 0;
    state.snapshot = { active: false, secondsRemaining: 0 };
    return;
  }
  state.deadline = Date.now() + seconds * 1000;
  state.snapshot = { active: true, secondsRemaining: Math.ceil(seconds) };
}

/** 仅供测试：停表并复位。 */
export function disposeRateLimit(): void {
  clearRateLimit();
}
